import { getXValue } from './get-x-value.js';
import { getYValue } from './get-y-value.js';
import { isThresholdDynamic, isThresholdXStatic } from './type-guards.js';
import { axisPositionIncludes, xAxisPositionIncludes } from './utils.js';
import { THRESHOLD_BAR_THICKNESS } from '../../../core/components/threshold-bar/types/constants.js';
import { isPointThreshold } from '../../../core/types/type-guards.js';
import { getValidColor } from '../../../core/utils/getValidColor.js';
import { NoThresholdData } from '../../context/XYChartThresholds.context.js';
import {
  castDynamicThresholdsDataType,
  castStaticThresholdsDataType,
  castStaticXThresholdsDataType,
} from '../../processing-strategies/utils/cast-data/cast-data-types.js';
import type { XYAxisAnyNumericalType } from '../../types/axis.js';
import { isAnyNumericalAxis } from '../../types/guards.js';
import type {
  XYChartAxisInternal,
  XYChartSlots,
  XYChartThresholdsInternal,
  XYChartThresholdsPropsInternal,
} from '../../types/xy-chart-internals.js';
import type {
  XYChartThresholdDynamicProps,
  XYChartThresholdIndicatorProps,
  XYChartThresholdStaticProps,
  _XYChartThresholdStaticXProps,
} from '../../types/xy-chart-props.js';
import type {
  XYChartXAxisPosition,
  XYChartYAxisPosition,
} from '../../types/xy-chart.js';

type BuildThresholds = (
  yAxes: {
    id: XYChartAxisInternal['id'];
    position: XYChartYAxisPosition;
    type: XYChartAxisInternal['type'];
  }[],
  xAxes: {
    id: XYChartAxisInternal['id'];
    reversed?: XYChartAxisInternal['reversed'];
    position: XYChartXAxisPosition;
    type: XYChartAxisInternal['type'];
  }[],
  thresholdSlots: XYChartSlots['thresholds'],
) => XYChartThresholdsInternal;

type NumericalXAxisMetadata = Record<
  string,
  {
    position: XYChartXAxisPosition;
    type: XYAxisAnyNumericalType;
    reversed: XYChartAxisInternal['reversed'];
  }
>;

type NumericalYAxisMetadata = Record<
  string,
  {
    position: XYChartYAxisPosition;
    type: XYAxisAnyNumericalType;
  }
>;

function getStrokeOnly(
  threshold: XYChartThresholdStaticProps | XYChartThresholdDynamicProps,
) {
  const firstData = isThresholdDynamic(threshold)
    ? threshold.data[0]
    : threshold.data;

  const firstMarkerDomain = {
    y0: getYValue(firstData, 'min'),
    y1: getYValue(firstData, 'max'),
  };

  return (
    (isPointThreshold(firstMarkerDomain.y0, firstMarkerDomain.y1) ||
      threshold.strokeOnly) ??
    false
  );
}

function getStrokeOnlyX(threshold: _XYChartThresholdStaticXProps) {
  const firstMarkerDomain = {
    x0: getXValue(threshold.data, 'min'),
    x1: getXValue(threshold.data, 'max'),
  };

  return (
    (isPointThreshold(firstMarkerDomain.x0, firstMarkerDomain.x1) ||
      threshold.strokeOnly) ??
    false
  );
}

/**
 * Checks if the Thresholds has valid axes.
 *
 * @param threshold - The threshold object containing `xAxisId` and `yAxisId`.
 * @param xAxisMetadata - Metadata for the x-axes, keyed by ID.
 * @param yAxisMetadata - Metadata for the y-axes, keyed by ID.
 * @returns `true` if both axis IDs are valid and they are not Categorical, `false` otherwise.
 */
function hasValidAxes(
  threshold: XYChartThresholdIndicatorProps | _XYChartThresholdStaticXProps,
  yAxisMetadata: NumericalYAxisMetadata,
  xAxisMetadata: NumericalXAxisMetadata | undefined = undefined,
) {
  if (isThresholdXStatic(threshold)) {
    return !!xAxisMetadata && threshold.xAxisId in xAxisMetadata;
  }

  let xAxisValid = true;
  if (isThresholdDynamic(threshold) && xAxisMetadata) {
    xAxisValid =
      threshold.xAxisId !== undefined && threshold.xAxisId in xAxisMetadata;
  }

  return xAxisValid && threshold.yAxisId in yAxisMetadata;
}

export const buildThresholds: BuildThresholds = (
  yAxes,
  xAxes,
  thresholdSlots,
) => {
  const xAxisMetadata = xAxes.filter(isAnyNumericalAxis).reduce((acc, axis) => {
    acc[axis.id] = {
      position: axis.position,
      type: axis.type,
      reversed: axis.reversed,
    };
    return acc;
  }, {} as NumericalXAxisMetadata);

  const yAxisMetadata = yAxes.filter(isAnyNumericalAxis).reduce((acc, axis) => {
    acc[axis.id] = {
      position: axis.position,
      type: axis.type,
    };
    return acc;
  }, {} as NumericalYAxisMetadata);
  const leftAxisWithThresholds = new Set<string>();
  const rightAxisWithThresholds = new Set<string>();
  const topAxisWithThresholds = new Set<string>();
  const bottomAxisWithThresholds = new Set<string>();

  const thresholdsData = thresholdSlots.reduce(
    (acc: XYChartThresholdsPropsInternal, threshold) => {
      if (!hasValidAxes(threshold, yAxisMetadata, xAxisMetadata)) {
        return acc;
      }

      if (isThresholdXStatic(threshold)) {
        const xAxis = xAxisMetadata[threshold.xAxisId];
        const castedData = castStaticXThresholdsDataType(
          threshold.data,
          xAxis.type,
        );

        if (castedData) {
          if (xAxisPositionIncludes('top')(xAxis.position)) {
            topAxisWithThresholds.add(threshold.xAxisId);
          }
          if (xAxisPositionIncludes('bottom')(xAxis.position)) {
            bottomAxisWithThresholds.add(threshold.xAxisId);
          }

          acc.push({
            id: crypto.randomUUID(),
            xAxisId: threshold.xAxisId,
            color: getValidColor(threshold.color),
            position: xAxis.position,
            label: threshold.label,
            strokeOnly: getStrokeOnlyX(threshold),
            data: castedData,
          });
        }

        return acc;
      }

      {
        const yAxis = yAxisMetadata[threshold.yAxisId];

        const baseThresholdData = {
          id: crypto.randomUUID(),
          yAxisId: threshold.yAxisId,
          color: getValidColor(threshold.color),
          position: yAxis.position,
          label: threshold.label,
          strokeOnly: getStrokeOnly(threshold),
        };

        if (axisPositionIncludes('left')(yAxis.position)) {
          leftAxisWithThresholds.add(threshold.yAxisId);
        }
        if (axisPositionIncludes('right')(yAxis.position)) {
          rightAxisWithThresholds.add(threshold.yAxisId);
        }

        if (isThresholdDynamic(threshold)) {
          const xAxis = xAxisMetadata[threshold.xAxisId];
          const castedData = castDynamicThresholdsDataType(
            threshold.data,
            xAxis.type,
            yAxis.type,
          );

          if (castedData.length) {
            const dynamicData = xAxis.reversed
              ? castedData.toReversed()
              : castedData;

            acc.push({
              ...baseThresholdData,
              xAxisId: threshold.xAxisId,
              data: dynamicData,
            });
          }
        } else {
          const castedData = castStaticThresholdsDataType(
            threshold.data,
            yAxis.type,
          );

          if (castedData) {
            acc.push({
              ...baseThresholdData,
              data: castedData,
            });
          }
        }
      }

      return acc;
    },
    [],
  );

  if (!thresholdsData.length) {
    return NoThresholdData;
  }

  const thresholds: XYChartThresholdsInternal = {
    thresholds: thresholdsData,
    none: false,
    offset: {
      left: leftAxisWithThresholds.size ? THRESHOLD_BAR_THICKNESS : 0,
      right: rightAxisWithThresholds.size ? THRESHOLD_BAR_THICKNESS : 0,
      top: topAxisWithThresholds.size ? THRESHOLD_BAR_THICKNESS : 0,
      bottom: bottomAxisWithThresholds.size ? THRESHOLD_BAR_THICKNESS : 0,
    },
    yAxes: {
      left: leftAxisWithThresholds,
      right: rightAxisWithThresholds,
    },
    xAxes: {
      top: topAxisWithThresholds,
      bottom: bottomAxisWithThresholds,
    },
  };
  return thresholds;
};
